import { useEffect, useState } from "react";
import { Employees as EmployeesApi } from "../lib/api";
import { Plus, Pencil, Trash2, X, Save, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmProvider";
import { useDeepLink } from "../lib/deepLink";

const EMPTY = {
  first_name: "",
  last_name: "",
  pesel: "",
  birth_date: "",
  id_document_type: "dowod",
  id_document_series: "",
  id_document_number: "",
  address: "",
  position: "",
  hourly_rate: "",
};

const ID_DOC_TYPES = [
  { value: "dowod", label: "Dowód osobisty" },
  { value: "paszport", label: "Paszport" },
  { value: "karta_pobytu", label: "Karta pobytu" },
];

export default function EmployeesPage() {
  const { confirm } = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | employee
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const data = await EmployeesApi.list();
      setList(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);
  useDeepLink("focus", !loading && list.length > 0, (id) => {
    const emp = list.find((e) => e.id === id);
    if (emp) startEdit(emp);
  });

  const startNew = () => {
    setForm(EMPTY);
    setEditing({});
  };
  const startEdit = (emp) => {
    setForm({
      first_name: emp.first_name || "",
      last_name: emp.last_name || "",
      pesel: emp.pesel || "",
      birth_date: emp.birth_date || "",
      id_document_type: emp.id_document_type || "dowod",
      id_document_series: emp.id_document_series || "",
      id_document_number: emp.id_document_number || "",
      address: emp.address || "",
      position: emp.position || "",
      hourly_rate: emp.hourly_rate ?? "",
    });
    setEditing(emp);
  };
  const cancel = () => setEditing(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name) {
      toast.error("Imię i nazwisko są wymagane");
      return;
    }
    const payload = {
      ...form,
      hourly_rate: form.hourly_rate === "" ? null : Number(form.hourly_rate),
    };
    try {
      if (editing && editing.id) {
        await EmployeesApi.update(editing.id, payload);
        toast.success("Pracownik zaktualizowany");
      } else {
        await EmployeesApi.create(payload);
        toast.success("Pracownik dodany");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error("Błąd zapisu: " + (err.response?.data?.detail || err.message));
    }
  };

  const remove = async (id) => {
    if (!(await confirm("Usunąć tego pracownika?", { tone: "danger", confirmLabel: "Usuń" }))) return;
    try {
      await EmployeesApi.remove(id);
      toast.success("Pracownik usunięty");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-8" data-testid="employees-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-label mb-1">// Baza pracowników</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
            Pracownicy
          </h1>
          <p className="text-zinc-600 mt-2 text-sm">
            Dane pracowników wykorzystywane wielokrotnie przy generowaniu umów.
          </p>
        </div>
        <button
          onClick={startNew}
          data-testid="btn-add-employee"
          className="inline-flex items-center gap-2 bg-[#E52C34] text-white px-5 py-3 font-bold uppercase tracking-wider hover:bg-[#C5232A] transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Nowy Pracownik
        </button>
      </div>

      <div className="border border-zinc-900 overflow-x-auto">
        <table className="w-full" data-testid="employees-table">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <Th>Imię i nazwisko</Th>
              <Th>PESEL</Th>
              <Th>Stanowisko</Th>
              <Th>Stawka /h</Th>
              <Th>Adres</Th>
              <Th className="text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-zinc-500 text-sm">
                  Ładowanie...
                </td>
              </tr>
            )}
            {!loading && list.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="py-12 text-center text-zinc-500 text-sm"
                  data-testid="empty-employees"
                >
                  Brak pracowników. Dodaj pierwszego klikając „Nowy Pracownik”.
                </td>
              </tr>
            )}
            {list.map((emp) => (
              <tr
                key={emp.id}
                className="border-t border-zinc-200 hover:bg-zinc-50"
                data-testid={`emp-row-${emp.id}`}
              >
                <td className="py-3 px-4 font-semibold">
                  {emp.first_name} {emp.last_name}
                </td>
                <td className="py-3 px-4 font-mono text-sm">{emp.pesel || "—"}</td>
                <td className="py-3 px-4 text-sm">{emp.position || "—"}</td>
                <td className="py-3 px-4 font-mono text-sm">
                  {emp.hourly_rate != null ? `${emp.hourly_rate.toFixed(2)} zł` : "—"}
                </td>
                <td className="py-3 px-4 text-sm text-zinc-600 max-w-xs truncate">
                  {emp.address || "—"}
                </td>
                <td className="py-3 px-4 text-right">
                  <button
                    onClick={() => startEdit(emp)}
                    data-testid={`btn-edit-${emp.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900 mr-2"
                  >
                    <Pencil className="w-3 h-3" /> Edytuj
                  </button>
                  <button
                    onClick={() => remove(emp.id)}
                    data-testid={`btn-delete-${emp.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 text-red-600 hover:border-red-600"
                  >
                    <Trash2 className="w-3 h-3" /> Usuń
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drawer / Modal */}
      {editing !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end"
          onClick={cancel}
          data-testid="employee-form-overlay"
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-lg h-full border-l border-zinc-900 p-8 overflow-y-auto"
            data-testid="employee-form"
          >
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="font-label">// {editing.id ? "Edycja" : "Nowy"}</div>
                <h2 className="font-heading font-black text-2xl uppercase tracking-tight">
                  {editing.id ? "Edytuj pracownika" : "Dodaj pracownika"}
                </h2>
              </div>
              <button
                type="button"
                onClick={cancel}
                data-testid="btn-close-form"
                className="p-2 hover:bg-zinc-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Imię" required>
                <input
                  data-testid="input-first-name"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  required
                />
              </Field>
              <Field label="Nazwisko" required>
                <input
                  data-testid="input-last-name"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  required
                />
              </Field>
              <Field label="PESEL">
                <input
                  data-testid="input-pesel"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
                  value={form.pesel}
                  maxLength={11}
                  onChange={(e) => setForm({ ...form, pesel: e.target.value })}
                />
              </Field>
              <Field label="Data urodzenia">
                <input
                  type="date"
                  data-testid="input-birth-date"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
                  value={form.birth_date}
                  onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                />
              </Field>
              <Field label="Typ dokumentu tożsamości" className="col-span-2">
                <select
                  data-testid="input-id-doc-type"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                  value={form.id_document_type}
                  onChange={(e) => setForm({ ...form, id_document_type: e.target.value })}
                >
                  {ID_DOC_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Seria dokumentu">
                <input
                  data-testid="input-id-doc-series"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono uppercase focus:outline-none focus:border-[#E52C34]"
                  value={form.id_document_series}
                  maxLength={5}
                  onChange={(e) => setForm({ ...form, id_document_series: e.target.value.toUpperCase() })}
                  placeholder="ABC"
                />
              </Field>
              <Field label="Numer dokumentu">
                <input
                  data-testid="input-id-doc-number"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
                  value={form.id_document_number}
                  maxLength={12}
                  onChange={(e) => setForm({ ...form, id_document_number: e.target.value })}
                  placeholder="123456"
                />
              </Field>
              <Field label="Adres zamieszkania" className="col-span-2">
                <input
                  data-testid="input-address"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="ul. Przykładowa 1, 70-001 Szczecin"
                />
              </Field>
              <Field label="Stanowisko">
                <input
                  data-testid="input-position"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                  value={form.position}
                  onChange={(e) => setForm({ ...form, position: e.target.value })}
                  placeholder="Kierowca"
                />
              </Field>
              <Field label="Stawka godzinowa (zł)">
                <input
                  data-testid="input-hourly-rate"
                  type="number"
                  step="0.01"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
                  value={form.hourly_rate}
                  onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                />
              </Field>
            </div>

            <div className="mt-10 flex gap-3">
              <button
                type="submit"
                data-testid="btn-save-employee"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#E52C34] text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-[#C5232A] transition-colors"
              >
                <Save className="w-4 h-4" />
                Zapisz
              </button>
              <button
                type="button"
                onClick={cancel}
                className="px-6 py-3 font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
              >
                Anuluj
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`py-3 px-4 text-xs uppercase tracking-[0.2em] font-bold ${className}`}>
      {children}
    </th>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-label block mb-2">
        {label}
        {required && <span className="text-[#E52C34] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
